/**
 * TD Product Form Component
 * Handles size selection, flavour variant selection, and add to cart functionality
 */

if (!customElements.get('td-product-form')) {
  class TdProductForm extends HTMLElement {
    constructor() {
      super();
      // State only - no DOM access; elements are not guaranteed to exist in constructor
      this.variantsData = null;
      this.sizeCards = null;
      this.selectedQuantity = 1;
      this.selectedVariantId = null;
      this.isVarietyPack = false;
      this.varietyPackVariantIds = [];
      this.isLoading = false;

      // Store bound function references for cleanup
      this.boundHandleSizeClick = this.handleSizeClick.bind(this);
      this.boundHandleSizeKeyDown = this.handleSizeKeyDown.bind(this);
      this.boundHandleAddToCart = this.handleAddToCart.bind(this);
      this.boundHandleFlavourChange = this.handleFlavourChange.bind(this);
    }

    connectedCallback() {
      // DOM refs only in connectedCallback - element is in the document with children
      this.sizeSelector = this.querySelector('.td-product-form__sizes');
      this.flavourPicker = this.querySelector('.td-product-form__flavours');
      this.addToCartBtn = this.querySelector('[data-add-to-cart]');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.priceDisplay = this.querySelector('[data-atc-price]');
      this.errorMessage = this.querySelector('[data-atc-error-message]');
      this.sizeCards = this.querySelectorAll('.td-size');

      this.loadVariantsData();
      this.bindEvents();
      this.initializeDefaultVariant();
      
      // Delay initial size selection to ensure all components (like flavor picker) are ready
      requestAnimationFrame(() => {
        this.initializeDefaultSelection();
      });
    }

    loadVariantsData() {
      const variantsScript = this.querySelector('[data-product-variants]');
      if (variantsScript) {
        try {
          this.variantsData = JSON.parse(variantsScript.textContent);
        } catch (e) {
          console.error('Failed to parse variants data:', e);
        }
      }
    }

    bindEvents() {
      // Handle size card clicks and keyboard events
      this.sizeCards.forEach(card => {
        card.addEventListener('click', this.boundHandleSizeClick);
        card.addEventListener('keydown', this.boundHandleSizeKeyDown);
      });

      // Handle add to cart
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', this.boundHandleAddToCart);
      }

      // Listen for flavour changes
      this.addEventListener('td-flavour-change', this.boundHandleFlavourChange);
    }

    disconnectedCallback() {
      // Remove event listeners to prevent memory leaks
      if (this.sizeCards) {
        this.sizeCards.forEach(card => {
          card.removeEventListener('click', this.boundHandleSizeClick);
          card.removeEventListener('keydown', this.boundHandleSizeKeyDown);
        });
      }

      if (this.addToCartBtn) {
        this.addToCartBtn.removeEventListener('click', this.boundHandleAddToCart);
      }

      this.removeEventListener('td-flavour-change', this.boundHandleFlavourChange);
    }

    initializeDefaultSelection() {
      // Select size card based on default_selected setting, or first card if none set
      if (this.sizeCards.length > 0) {
        const selected = this.querySelector('.td-size--selected');
        if (!selected) {
          // Look for a card with data-default-selected attribute
          const defaultCard = this.querySelector('.td-size[data-default-selected="true"]');
          if (defaultCard) {
            this.selectSize(defaultCard);
          } else {
            // Fall back to first card
            this.selectSize(this.sizeCards[0]);
          }
        } else {
          // Ensure aria-pressed is set correctly for pre-selected card
          this.sizeCards.forEach(card => {
            if (card === selected) {
              card.setAttribute('aria-pressed', 'true');
            } else {
              card.setAttribute('aria-pressed', 'false');
            }
          });
          this.updateQuantityFromCard(selected);
          this.updatePriceDisplay(selected);
        }
      }
    }

    initializeDefaultVariant() {
      // Get the initially selected variant from the hidden input
      if (this.variantInput) {
        this.selectedVariantId = this.variantInput.value;
      }
      
      // If no variant set, get from selected flavour input
      if (!this.selectedVariantId) {
        const selectedFlavour = this.querySelector('.td-flavour-picker__input:checked');
        if (selectedFlavour && selectedFlavour.dataset.variantId) {
          this.selectedVariantId = selectedFlavour.dataset.variantId;
        }
      }
    }

    handleSizeClick(event) {
      const card = event.currentTarget;
      this.selectSize(card);
    }

    handleSizeKeyDown(event) {
      // Handle Enter and Space key activation
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const card = event.currentTarget;
        this.selectSize(card);
      }
    }

    selectSize(card) {
      // Remove selected class and aria-pressed from all cards
      this.sizeCards.forEach(c => {
        c.classList.remove('td-size--selected');
        c.setAttribute('aria-pressed', 'false');
      });
      
      // Add selected class and aria-pressed to selected card
      card.classList.add('td-size--selected');
      card.setAttribute('aria-pressed', 'true');
      
      // Update quantity
      this.updateQuantityFromCard(card);
      
      // Update header display
      this.updateSizeHeader(card);

      // Update price display
      this.updatePriceDisplay(card);

      // Dispatch custom event on document so all components receive it
      document.dispatchEvent(new CustomEvent('td-size-change', {
        bubbles: true,
        detail: {
          quantity: this.selectedQuantity,
          title: card.querySelector('.td-size__title')?.textContent || ''
        }
      }));
    }

    updateQuantityFromCard(card) {
      const quantity = parseInt(card.dataset.quantity, 10) || 1;
      this.selectedQuantity = quantity;
    }

    updateSizeHeader(card) {
      const headerValue = this.querySelector('[data-selected-size]');
      if (headerValue) {
        const quantity = parseInt(card.dataset.quantity, 10) || 1;
        const pouchLabel = quantity === 1 ? 'Pouch' : 'Pouches';
        headerValue.textContent = quantity + ' ' + pouchLabel;
      }
    }

    updatePriceDisplay(card) {
      if (!this.priceDisplay) return;
      
      const priceInCents = parseInt(card.dataset.price, 10) || 0;
      const formattedPrice = this.formatMoney(priceInCents);
      this.priceDisplay.textContent = formattedPrice;
    }

    formatMoney(cents) {
      // Format cents to dollars without trailing zeros
      const dollars = cents / 100;
      if (dollars % 1 === 0) {
        return '$' + dollars.toFixed(0);
      }
      return '$' + dollars.toFixed(2);
    }

    handleFlavourChange(event) {
      const { variantId, isVarietyPack, variantIds } = event.detail;
      
      // Track variety pack selection
      this.isVarietyPack = isVarietyPack || false;
      this.varietyPackVariantIds = variantIds || [];
      
      if (variantId) {
        this.selectedVariantId = variantId;
        
        // Find the variant data
        const variant = this.variantsData?.find(v => v.id.toString() === variantId.toString());
        
        // Update hidden input
        if (this.variantInput) {
          this.variantInput.value = variantId;
          
          // Check variant availability
          if (variant) {
            this.variantInput.disabled = !variant.available;
          }
        }

        // Recalculate all size card prices with the new variant price
        if (variant) {
          this.updateAllPrices(variant.price);
        }
      }
    }

    updateAllPrices(variantPriceInCents) {
      // Update each size card's prices
      this.sizeCards.forEach(card => {
        const quantity = parseInt(card.dataset.quantity, 10) || 1;
        const discountSource = card.dataset.discountSource || 'percentage';
        const discountPercent = parseFloat(card.dataset.discountPercent) || 0;
        const discountDollar = parseInt(card.dataset.discountDollar, 10) || 0;
        const sticksPerPouch = parseInt(card.dataset.sticksPerPouch, 10) || 15;
        const badgeDisplay = card.dataset.badgeDisplay || 'percentage';

        // Calculate base total price
        const baseTotalPrice = variantPriceInCents * quantity;

        // Apply discount
        let totalPrice = baseTotalPrice;
        let discountDollarAmount = 0;
        let discountPercentValue = 0;
        let hasDiscount = false;

        if (discountSource === 'percentage' && discountPercent > 0) {
          hasDiscount = true;
          discountPercentValue = discountPercent;
          totalPrice = Math.floor(baseTotalPrice * (100 - discountPercent) / 100);
          discountDollarAmount = baseTotalPrice - totalPrice;
        } else if (discountSource === 'dollar' && discountDollar > 0) {
          hasDiscount = true;
          discountDollarAmount = discountDollar;
          totalPrice = baseTotalPrice - discountDollar;
          // Match Liquid's integer division: (discountDollar * 100) / baseTotalPrice
          discountPercentValue = Math.floor((discountDollar * 100) / baseTotalPrice);
        }

        // Calculate price per stick
        // Match Liquid's integer division (divided_by truncates, doesn't round)
        const totalSticks = quantity * sticksPerPouch;
        const pricePerStickCents = Math.floor(totalPrice / totalSticks);

        // Update data-price attribute
        card.dataset.price = totalPrice;

        // Update strikethrough price (original price before discount)
        const originalPriceEl = card.querySelector('[data-size-original-price]');
        if (originalPriceEl) {
          // Show strikethrough only if prices differ and discount exists
          if (hasDiscount && baseTotalPrice !== totalPrice) {
            originalPriceEl.textContent = this.formatMoney(baseTotalPrice);
            originalPriceEl.style.display = '';
          } else {
            originalPriceEl.style.display = 'none';
          }
        }

        // Update total price display
        const totalPriceEl = card.querySelector('[data-size-total-price]');
        if (totalPriceEl) {
          totalPriceEl.textContent = this.formatMoney(totalPrice);
        }

        // Update price per stick display (may exist in price area and/or pre-layout row)
        // Use truncated cents to match Liquid's divided_by behavior
        const pricePerStickEls = card.querySelectorAll('[data-size-price-per-stick]');
        pricePerStickEls.forEach(el => {
          el.textContent = this.formatMoneyWithCents(pricePerStickCents) + '/stick';
        });

        // Update discount badges (mobile and desktop)
        const discountBadges = card.querySelectorAll('[data-size-discount]');
        discountBadges.forEach(discountBadge => {
          if (hasDiscount) {
            discountBadge.classList.remove('td-size__discount--hidden');
            if (badgeDisplay === 'percentage') {
              // discountPercentValue is already floored to match Liquid's integer division
              discountBadge.textContent = 'SAVE ' + discountPercentValue + '%';
            } else {
              discountBadge.textContent = 'SAVE ' + this.formatMoney(discountDollarAmount);
            }
          } else {
            discountBadge.classList.add('td-size__discount--hidden');
          }
        });
      });

      // Update ATC button price for selected card
      const selectedCard = this.querySelector('.td-size--selected');
      if (selectedCard) {
        this.updatePriceDisplay(selectedCard);
      }
    }

    formatMoneyWithCents(cents) {
      // Format cents to dollars with cents
      const dollars = cents / 100;
      return '$' + dollars.toFixed(2);
    }

    showErrorMessage(message) {
      if (this.errorMessage) {
        this.errorMessage.textContent = message;
        this.errorMessage.style.display = 'block';
      }
    }

    hideErrorMessage() {
      if (this.errorMessage) {
        this.errorMessage.style.display = 'none';
        this.errorMessage.textContent = '';
      }
    }

    async handleAddToCart(event) {
      event.preventDefault();
      
      if (this.isLoading) return;
      
      // Hide any previous error messages
      this.hideErrorMessage();
      
      // Validate we have a variant selected
      if (!this.selectedVariantId && !this.isVarietyPack) {
        this.showErrorMessage('Please select a flavor option.');
        return;
      }

      this.isLoading = true;
      this.updateButtonState('loading');

      try {
        let data;
        
        if (this.isVarietyPack && this.varietyPackVariantIds.length === 2) {
          // Variety pack: add half quantity of each variant
          const halfQuantity = Math.floor(this.selectedQuantity / 2);
          
          // Build items array for both variants
          const items = this.varietyPackVariantIds.map(variantId => ({
            id: parseInt(variantId, 10),
            quantity: halfQuantity
          }));

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ items })
          });

          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }

          data = await response.json();
        } else {
          // Regular single variant add
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              id: this.selectedVariantId,
              quantity: this.selectedQuantity
            })
          });

          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }

          data = await response.json();
        }
        
        // Success - update cart count and show feedback
        this.updateButtonState('success');
        this.hideErrorMessage();
        
        // Fetch updated cart and dispatch cart:update event to open cart drawer
        await this.updateCartAndOpenDrawer();
        
        // Dispatch success event
        this.dispatchEvent(new CustomEvent('td-add-to-cart-success', {
          bubbles: true,
          detail: {
            variantId: this.isVarietyPack ? this.varietyPackVariantIds : this.selectedVariantId,
            quantity: this.selectedQuantity,
            isVarietyPack: this.isVarietyPack,
            item: data
          }
        }));

        // Reset button after delay
        setTimeout(() => {
          this.updateButtonState('default');
        }, 2000);

      } catch (error) {
        console.error('Add to cart error:', error);
        this.updateButtonState('error');
        this.showErrorMessage('Unable to add item to cart. Please try again.');
        
        setTimeout(() => {
          this.updateButtonState('default');
          this.hideErrorMessage();
        }, 2000);
      } finally {
        this.isLoading = false;
      }
    }

    updateButtonState(state) {
      if (!this.addToCartBtn) return;
      
      const textEl = this.addToCartBtn.querySelector('.td-add-to-cart__text');
      const originalText = this.addToCartBtn.dataset.originalText || textEl?.textContent || 'Add to Cart';
      
      // Store original text
      if (!this.addToCartBtn.dataset.originalText) {
        this.addToCartBtn.dataset.originalText = originalText;
      }

      switch (state) {
        case 'loading':
          this.addToCartBtn.disabled = true;
          if (textEl) textEl.textContent = 'Adding...';
          break;
        case 'success':
          this.addToCartBtn.disabled = false;
          if (textEl) textEl.textContent = 'Added!';
          break;
        case 'error':
          this.addToCartBtn.disabled = false;
          if (textEl) textEl.textContent = 'Error - Try Again';
          break;
        default:
          this.addToCartBtn.disabled = false;
          if (textEl) textEl.textContent = originalText;
      }
    }

    async updateCartAndOpenDrawer() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        // Dispatch cart:update event - this is what the theme's cart drawer listens for
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: this.dataset.sectionId,
            data: {
              itemCount: cart.item_count,
              variantId: this.selectedVariantId
            }
          }
        }));
        
        // Try to update cart icon count if it exists
        const cartCountElements = document.querySelectorAll('[data-cart-count]');
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count;
        });

        // Directly open the cart drawer
        this.openCartDrawer();
      } catch (error) {
        console.error('Failed to update cart:', error);
      }
    }

    openCartDrawer() {
      // Try to find and open the cart drawer component
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer) {
        // Use the open method if available
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else if (typeof cartDrawer.showDialog === 'function') {
          cartDrawer.showDialog();
        }
      }
    }
  }

  customElements.define('td-product-form', TdProductForm);
}
